import React, { useEffect, useRef, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import moment from "moment";
import IdleTimer from "react-idle-timer";
import styled from "styled-components";

// 컴포넌트
import { Header } from "../components/Header";
import IdleTimeOutModal from "../components/IdleTimeOutModal";
import { LoadingBackground } from "../components/LoadingBackground";
import DropzoneEditMap from "../components/DropzoneEditMap";

// 컨텍스트
import { useAuthContext } from "../domain/auth/context/useAuthContext";

// 상수
import { MAIN_BAND_ID } from "../constants";

// 도메인별로 정리된 페이지 임포트
// 인증 도메인
import { OTPRegisterPage } from "../domain/auth/page/OTPRegisterPage";

// 영역 도메인
import { AreaNormalListPage } from "../domain/areas/page/AreaNormalListPage";
import { AreaEditPage } from "../domain/areas/page/AreaEditPage";
import { AreaCollectingListPage } from "../domain/areas/page/AreaCollectingListPage";
import { AreaRidesPage } from "../domain/areas/page/AreaRidesPage";

// 스쿠터 도메인
import Scoots from "../domain/scooters/page/Scoots";
import Moped from "../domain/scooters/page/Moped";
import { CollectAreaPage } from "../domain/scooters/page/CollectAreaPage";

// 라이드 도메인
import Ride from "../domain/rides/pages/Ride";

// 배터리 도메인
import Battery from "../domain/batteries/pages/Battery";
import BatteryDetail from "../domain/batteries/pages/BatteryDetail";

// 관리자 도메인
import Admin from "../domain/admin/pages/Admin";
import Collector from "../domain/collector/pages/Collector";
import { DropzoneEditMapPage } from "../domain/collector/pages/DropzoneEditMapPage";

// 밴드 도메인
import Bands from "../domain/bands/pages/Bands";

// 창고 도메인
import Warehouse from "../domain/warehouse/page/Warehouse";

// 벤더 드라이버 도메인
import { VendorDriverPage } from "../domain/vendor_driver/page/VendorDriverPage";
import { VendorDriverDetailPage } from "../domain/vendor_driver/page/VendorDriverDetailPage";

// 대시보드 도메인
import SalesPage from "../domain/dashboards/pages/SalesPage";

// 드롭존 도메인
import DropzoneMapPage from "../domain/dropzone/page/DropzoneMapPage";
import { DropzoneListPage } from "../domain/dropzone/page/DropzoneListPage";

// QR 이력 도메인
import QrHistory from "../domain/qr_histories/page/QrHistory";

// 미결제 도메인
import NotPaid from "../domain/not_paid/pages/NotPaid";

// 중고 스쿠터 도메인
import UsedScoot from "../domain/used_scoots/pages/UsedScoot";

// 사용자 상세 도메인
import UserDetail from "../domain/user_detail/pages/UserDetail";

// 이력 도메인
import { HistoryListPage } from "../domain/history/page/HistoryListPage";

// 저장소 도메인
import { StorageStatusPage } from "../domain/storage/pages/StorageStatusPage";
import { StorageHistoryPage } from "../domain/storage/pages/StorageHistoryPage";

// 시냅스 도메인
import { Camp } from "../domain/synapse/pages/Camp";
import { CollectAreaMapPage } from "../domain/synapse/pages/CollectAreaMapPage";
import { DeploymentAreaListPage } from "../domain/synapse/pages/DeploymentAreaListPage";
import { DeploymentAreaMapPage } from "../domain/synapse/pages/DeploymentAreaMapPage";
import { CollectAreaListPage } from "../domain/synapse/pages/CollectAreaListPage";
import { CampScheduleRegistrationPage } from "../domain/synapse/pages/CampScheduleRegistrationPage";
import { RecommendedJobPage } from "../domain/synapse/pages/RecommendedJobPage";

// 작업 도메인
import { TaskManagementPage } from "../domain/task/pages/TaskManagementPage";
import { TaskManagementDetailPage } from "../domain/task/pages/TaskManagementDetailPage";

// 사용자 도메인
import { Users } from "../domain/users/page/Users";

// 새 수집기 맵 도메인
import { NewCollectorMap } from "../domain/new-collector-map/pages/NewCollectorMap";

// 견인 도메인
import { TowRegister } from "../domain/tow/pages/TowRegister";

// 판매 도메인
import { DataSalesPage } from "../domain/sales/pages/DataSalesPage";

// 기타 컨테이너
import ScootDetail from "./ScootDetail";
import RouteMap from "./RouteMap";
import LandingPage from "./LandingPage";
import { CollectorFilterMap } from "./CollectorFilterMap";
import { CollectorMap } from "./CollectorMap";
import { logAdminRoute } from "../domain/common/services/log.service";

const Container = styled.div`
  width: 100%;
`;

const ContentContainer = styled.div`
  width: 100%;
`;

/**
 * 라우팅과 인증을 처리하는 메인 레이아웃 컴포넌트
 */
const Layout = () => {
  const location = useLocation();
  const idleTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const { auth, logout, authLoading, fetch, search, setSearch } =
    useAuthContext();

  // 유휴 시간 초과 처리
  const onIdle = () => {
    setOpen(true);
  };

  // 사용자 액션 시 타이머 리셋
  const onAction = () => {
    const idToken = localStorage.getItem("accessToken");
    localStorage.setItem("time", moment().toISOString());
    if (!idToken) {
      localStorage.clear();
    }
  };

  // 라우트 변경 로깅
  const logRouteChange = async (path) => {
    try {
      await logAdminRoute(path);
    } catch (error) {
      console.error("Failed to log route change:", error);
    }
  };

  // 위치 변경 시 검색 업데이트
  useEffect(() => {
    setSearch(location.search);
  }, [location, setSearch]);

  // // 검색 변경 시 인증 정보 가져오기
  useEffect(() => {
    if (!search) fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, location]);

  // 위치가 변경될 때마다 라우트 변경 로깅
  useEffect(() => {
    if (location.pathname) {
      logRouteChange(location.pathname + location.search);
    }
  }, [location.pathname, location.search]);

  if (authLoading || !auth) return <LoadingBackground />;

  // 보호된 라우트를 위한 프라이빗 라우트 컴포넌트
  const PrivateRoute = ({ component, ...rest }) => {
    return (
      <Route
        {...rest}
        render={(props) =>
          auth.band.id === MAIN_BAND_ID
            ? React.createElement(component, props)
            : (alert("권한이 없습니다. 본사에 문의해주세요."),
              (
                <Redirect
                  to={{
                    pathname: "/app/landing",
                    state: {
                      from: props.location,
                    },
                  }}
                />
              ))
        }
      />
    );
  };

  return (
    <Container>
      <IdleTimer
        ref={idleTimer}
        element={document}
        onIdle={onIdle}
        onAction={onAction}
        onEvent
        debounce={250}
        timeout={1000 * 60 * 60 * 3}
      >
        <Header />
        <ContentContainer>
          <Switch>
            {/* 인증 */}
            <Route path={"/app/otp/register"} component={OTPRegisterPage} />

            {/* 랜딩 페이지 */}
            <Route path={"/app/landing"} component={LandingPage} />

            {/* 결제 */}
            <Route path={"/app/not-paid"} component={NotPaid} />

            {/* 대시보드 */}
            <Route path={"/app/sales/dashboard"} component={DataSalesPage} />
            <Route path={"/app/v3/dashboard"} component={SalesPage} />

            {/* 스쿠터 */}
            <Route exact path={"/app/scoot"} component={Scoots} />
            <Route path={"/app/used"} component={UsedScoot} />
            <Route exact path={"/app/moped"} component={Moped} />
            <Route path={"/app/scoot/history"} component={HistoryListPage} />
            <Route path={"/app/scoot/history-qr"} component={QrHistory} />
            <Route path={"/app/scoot/:imei"} component={ScootDetail} />

            {/* 시냅스 */}
            <Route exact path={"/app/synapse/camp"} component={Camp} />
            <Route
              exact
              path={"/app/synapse/camp/schedule-registration"}
              component={CampScheduleRegistrationPage}
            />
            <Route
              exact
              path={"/app/synapse/collect"}
              component={CollectAreaMapPage}
            />
            <Route
              exact
              path={"/app/synapse/collect-list"}
              component={CollectAreaListPage}
            />
            <Route
              exact
              path={"/app/synapse/deployment"}
              component={DeploymentAreaMapPage}
            />
            <Route
              exact
              path={"/app/synapse/deployment-list"}
              component={DeploymentAreaListPage}
            />
            <Route path={"/app/synapse/recommended-job"} component={RecommendedJobPage} />

            {/* 작업 관리 */}
            <Route exact path={"/app/task"} component={TaskManagementPage} />
            <Route
              path={"/app/task/:id"}
              component={TaskManagementDetailPage}
            />

            {/* 견인 관리 */}
            <Route path={"/app/tow/register"} component={TowRegister} />
            {/* <Route exact path={"/app/tow/list"} component={TowList} /> */}
            {/* <Route exact path={"/app/tow/statistics"} component={TowStatistics} /> */}

            {/* 배터리 */}
            <Route exact path={"/app/battery"} component={Battery} />
            <Route path={"/app/battery/:identity"} component={BatteryDetail} />

            {/* 라이드 */}
            <Route path={"/app/ride"} component={Ride} />

            {/* 지오펜스 */}
            <Route exact path={"/app/area/pick"} component={AreaCollectingListPage} />
            <Route exact path={"/app/area/operation"} component={AreaNormalListPage} />
            <Route exact path={"/app/area/edit-new"} component={AreaEditPage} />
            <Route exact path={"/app/area/collect-map"} component={CollectAreaPage} />
            <Route exact path={"/app/area/collector-area"} component={CollectorFilterMap} />
            <Route exact path={"/app/area/collector-map"} component={CollectorMap} />
            <Route exact path={"/app/area/new-collector-map"} component={NewCollectorMap} />
            <Route exact path={"/app/area/rides"} component={AreaRidesPage} />

            {/* 드롭존 */}
            <Route path={"/app/dropzone/map"} component={DropzoneEditMap} />
            <Route path={"/app/dropzone/map-new"} component={DropzoneMapPage} />
            <Route
              path={"/app/dropzone-edit-map"}
              component={DropzoneEditMapPage}
            />
            <Route path={"/app/dropzone"} component={DropzoneListPage} />
            <Route path={"/app/map/dropzone"} component={DropzoneListPage} />

            {/* 저장소 관리 */}
            <Route path={"/app/storage/status"} component={StorageStatusPage} />
            <Route
              path={"/app/storage/history"}
              component={StorageHistoryPage}
            />

            {/* 사용자 */}
            <PrivateRoute exact path={"/app/user"} component={Users} />
            <PrivateRoute path={"/app/user/:userId"} component={UserDetail} />

            {/* 계정 */}
            <Route path={"/app/permission/admin"} component={Admin} />
            <Route path={"/app/permission/collector"} component={Collector} />

            {/* 기타 */}
            <Route exact path={"/app/band/create"} component={Bands} />
            <Route path={"/app/band/warehouse"} component={Warehouse} />

            <Route
              exact
              path={"/app/collectors"}
              component={VendorDriverPage}
            />
            <Route
              path={"/app/collectors/:id"}
              component={VendorDriverDetailPage}
            />
            
            {/* 기기 이동 경로 */}
            <Route path={"/app/map/route/:imei"} component={RouteMap} />
          </Switch>
        </ContentContainer>
      </IdleTimer>
      <IdleTimeOutModal logout={logout} open={open}></IdleTimeOutModal>
    </Container>
  );
};

export default Layout;
